import { useEffect, useMemo, useState } from 'react';
import { Bot, BookOpenText, CircleHelp, RotateCcw, Search, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Badge } from '@/components/ui/badge.jsx';

const DEFAULT_OPEN_SECTIONS = 2;

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function buildAssistantWelcome(entry) {
  return [
    {
      id: `${entry.id}-assistant-welcome`,
      role: 'assistant',
      text: `Estoy listo para ayudarte con ${entry.title.toLowerCase()}. Elegí una pregunta frecuente o buscá una palabra clave.`,
    },
  ];
}

function buildSearchIndex(entry) {
  const sectionResults = entry.sections.flatMap((section) => {
    const rows = [
      { label: 'Objetivo', text: section.objective },
      ...section.importantFields.map((item) => ({ label: 'Campo importante', text: item })),
      ...section.commonErrors.map((item) => ({ label: 'Error común', text: item })),
      ...section.recommendations.map((item) => ({ label: 'Recomendación', text: item })),
      ...section.faqs.map((item) => ({ label: 'Pregunta frecuente', text: item })),
      ...section.shortcuts.map((item) => ({ label: 'Atajo útil', text: item })),
    ];

    return rows.map((row, index) => ({
      id: `${section.id}-${index}`,
      type: 'section',
      sectionId: section.id,
      title: section.title,
      subtitle: row.label,
      text: row.text,
      keywords: section.keywords || [],
    }));
  });

  const assistantResults = entry.assistantQuestions.map((item) => ({
    id: item.id,
    type: 'assistant',
    title: item.question,
    subtitle: 'Respuesta sugerida',
    text: item.answer,
    keywords: item.keywords || [],
    question: item,
  }));

  return [...assistantResults, ...sectionResults];
}

function filterResults(results, query) {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) {
    return [];
  }

  return results.filter((item) => {
    const haystack = normalizeText([
      item.title,
      item.subtitle,
      item.text,
      ...(item.keywords || []),
    ].join(' '));
    return haystack.includes(normalizedQuery);
  });
}

function SurfaceCard({ children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.92)] shadow-[0_12px_28px_rgba(var(--shadow),0.1)] ${className}`}>
      {children}
    </div>
  );
}

function SectionList({ title, items = [] }) {
  if (!items.length) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">{title}</p>
      <ul className="mt-3 space-y-2">
        {items.map((item) => (
          <li key={item} className="rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--subpanel),0.18)] px-3 py-2 text-sm leading-6 text-[rgba(var(--fg),0.84)]">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function GuideTab({ entry }) {
  const [openSections, setOpenSections] = useState(() => new Set(entry.sections.slice(0, DEFAULT_OPEN_SECTIONS).map((section) => section.id)));

  useEffect(() => {
    setOpenSections(new Set(entry.sections.slice(0, DEFAULT_OPEN_SECTIONS).map((section) => section.id)));
  }, [entry]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <SurfaceCard className="p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl border border-[rgba(var(--brand),0.26)] bg-[rgba(var(--brand),0.1)] p-2.5 text-[rgb(var(--brand))]">
            <BookOpenText className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="rounded-full normal-case tracking-normal">{entry.tone}</Badge>
            </div>
            <p className="mt-3 text-sm leading-6 text-[rgba(var(--fg),0.84)]">{entry.intro}</p>
          </div>
        </div>
      </SurfaceCard>

      {entry.sections.map((section) => {
        const isOpen = openSections.has(section.id);
        return (
          <SurfaceCard key={section.id} className="overflow-hidden">
            <button
              type="button"
              className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left transition hover:bg-[rgba(var(--subpanel),0.32)]"
              onClick={() => toggleSection(section.id)}
              aria-expanded={isOpen}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[rgb(var(--fg))]">{section.title}</p>
                <p className="mt-1 text-sm leading-6 text-[rgba(var(--muted),0.95)]">{section.objective}</p>
              </div>
              <span className="rounded-full border border-[rgba(var(--border),0.34)] bg-[rgba(var(--panel),0.72)] px-2 py-1 text-xs font-semibold text-[rgba(var(--fg),0.82)]">
                {isOpen ? 'Ocultar' : 'Ver'}
              </span>
            </button>

            {isOpen ? (
              <div className="space-y-4 border-t border-[rgba(var(--border),0.24)] px-4 py-4">
                <SectionList title="Campos importantes" items={section.importantFields} />
                <SectionList title="Errores comunes" items={section.commonErrors} />
                <SectionList title="Recomendaciones" items={section.recommendations} />
                <SectionList title="Preguntas frecuentes" items={section.faqs} />
                <SectionList title="Atajos útiles" items={section.shortcuts} />
              </div>
            ) : null}
          </SurfaceCard>
        );
      })}
    </div>
  );
}

function AssistantTab({ entry, onSwitchToGuide }) {
  const [messages, setMessages] = useState(() => buildAssistantWelcome(entry));
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setMessages(buildAssistantWelcome(entry));
    setSearchQuery('');
  }, [entry]);

  const index = useMemo(() => buildSearchIndex(entry), [entry]);
  const results = useMemo(() => filterResults(index, searchQuery).slice(0, 8), [index, searchQuery]);

  const askQuestion = (question) => {
    // Asistencia local basada en reglas y contenido predefinido: no usa IA ni backend.
    setMessages((prev) => [
      ...prev,
      { id: `${question.id}-user-${prev.length}`, role: 'user', text: question.question },
      { id: `${question.id}-assistant-${prev.length}`, role: 'assistant', text: question.answer },
    ]);
  };

  const resetConversation = () => {
    setMessages(buildAssistantWelcome(entry));
    setSearchQuery('');
  };

  return (
    <div className="space-y-4">
      <SurfaceCard className="p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl border border-[rgba(var(--brand),0.26)] bg-[rgba(var(--brand),0.1)] p-2.5 text-[rgb(var(--brand))]">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[rgb(var(--fg))]">Asistente</p>
            <p className="mt-1 text-sm leading-6 text-[rgba(var(--muted),0.95)]">
              Responde con contenido local predefinido y sugerencias breves de uso operativo.
            </p>
          </div>
        </div>
      </SurfaceCard>

      <SurfaceCard className="p-4">
        <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]" htmlFor="help-assistant-search">
          <Search className="h-4 w-4" />
          Buscar en esta ayuda
        </label>
        <Input
          id="help-assistant-search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Ej.: localidad, vigencia, cliente pagador"
        />

        {searchQuery ? (
          <div className="mt-4 space-y-2">
            {results.length ? (
              results.map((result) => (
                <button
                  key={result.id}
                  type="button"
                  className="flex w-full flex-col items-start rounded-2xl border border-[rgba(var(--border),0.32)] bg-[rgba(var(--subpanel),0.16)] px-3 py-3 text-left transition hover:bg-[rgba(var(--subpanel),0.3)]"
                  onClick={() => {
                    if (result.type === 'assistant' && result.question) {
                      askQuestion(result.question);
                      return;
                    }
                    onSwitchToGuide();
                  }}
                >
                  <span className="text-sm font-semibold text-[rgb(var(--fg))]">{result.title}</span>
                  <span className="mt-1 text-xs uppercase tracking-[0.14em] text-[rgba(var(--muted),0.92)]">{result.subtitle}</span>
                  <span className="mt-2 text-sm leading-6 text-[rgba(var(--fg),0.82)]">{result.text}</span>
                </button>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[rgba(var(--border),0.4)] px-3 py-4 text-sm text-[rgba(var(--muted),0.95)]">
                No encontré coincidencias en esta ayuda.
              </div>
            )}
          </div>
        ) : null}
      </SurfaceCard>

      <SurfaceCard className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Preguntas sugeridas</p>
            <p className="mt-1 text-sm text-[rgba(var(--fg),0.82)]">Elegí una consulta típica de esta pantalla.</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={resetConversation} className="h-8 rounded-full px-3">
            <RotateCcw className="h-4 w-4" />
            Volver al inicio
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {entry.assistantQuestions.map((item) => (
            <button
              key={item.id}
              type="button"
              className="rounded-full border border-[rgba(var(--border),0.42)] bg-[rgba(var(--panel),0.92)] px-3 py-2 text-sm text-[rgba(var(--fg),0.84)] transition hover:border-[rgba(var(--brand),0.35)] hover:bg-[rgba(var(--brand),0.08)] hover:text-[rgb(var(--brand))]"
              onClick={() => askQuestion(item)}
            >
              {item.question}
            </button>
          ))}
        </div>
      </SurfaceCard>

      <SurfaceCard className="overflow-hidden">
        <div className="flex items-center gap-2 border-b border-[rgba(var(--border),0.24)] px-4 py-3">
          <Bot className="h-4 w-4 text-[rgb(var(--brand))]" />
          <p className="text-sm font-semibold text-[rgb(var(--fg))]">Conversación guiada</p>
        </div>
        <div className="max-h-[38vh] space-y-3 overflow-y-auto px-4 py-4 md:max-h-[42vh]">
          {messages.map((message) => (
            <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[88%] rounded-2xl px-3 py-3 text-sm leading-6 shadow-[0_8px_20px_rgba(var(--shadow),0.08)] ${
                  message.role === 'user'
                    ? 'border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]'
                    : 'border border-[rgba(var(--border),0.3)] bg-[rgba(var(--panel),0.96)] text-[rgba(var(--fg),0.84)]'
                }`}
              >
                {message.text}
              </div>
            </div>
          ))}
        </div>
      </SurfaceCard>
    </div>
  );
}

export default function HelpDrawer({
  open,
  onClose,
  activeTab,
  onTabChange,
  entry,
}) {
  const tabs = [
    { id: 'guide', label: 'Guía asistida', icon: BookOpenText },
    { id: 'assistant', label: 'Asistente', icon: CircleHelp },
  ];

  return (
    <>
      <div
        className={`fixed inset-0 z-[calc(var(--z-modal)-1)] bg-[rgba(var(--shadow),0.22)] transition-opacity duration-200 ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={`fixed right-0 top-0 z-[var(--z-modal)] flex h-dvh w-full max-w-[460px] flex-col border-l border-[rgba(var(--border),0.55)] bg-[rgb(var(--bg))] shadow-[0_24px_60px_rgba(var(--shadow),0.24)] transition-transform duration-200 md:w-[460px] ${open ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-drawer-title"
      >
        <div className="border-b border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.96)] px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full normal-case tracking-normal">{entry.tone}</Badge>
                <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.18)] px-2.5 py-1 text-[11px] font-semibold text-[rgba(var(--fg),0.82)]">
                  F1
                </span>
                <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.18)] px-2.5 py-1 text-[11px] font-semibold text-[rgba(var(--fg),0.82)]">
                  Esc
                </span>
              </div>
              <h2 id="help-drawer-title" className="mt-3 text-lg font-semibold text-[rgb(var(--fg))]">{entry.title}</h2>
              <p className="mt-1 text-sm leading-6 text-[rgba(var(--muted),0.95)]">{entry.summary}</p>
            </div>

            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar ayuda" className="shrink-0 rounded-full">
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="mt-4 flex gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const selected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onTabChange(tab.id)}
                  aria-pressed={selected}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition ${
                    selected
                      ? 'border border-[rgba(var(--brand),0.34)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]'
                      : 'border border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.88)] text-[rgba(var(--fg),0.84)] hover:bg-[rgba(var(--subpanel),0.34)]'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {activeTab === 'guide' ? (
            <GuideTab entry={entry} />
          ) : (
            <AssistantTab entry={entry} onSwitchToGuide={() => onTabChange('guide')} />
          )}
        </div>
      </aside>
    </>
  );
}
